import EAButton from 'components/common/EAButton';
import EAIcon from 'components/common/EAIcon';
import EATooltip from 'components/common/EATooltip';
import { getUrlWithAccountId } from 'config/Routes';
import i18next from 'i18next';
import { isEmpty } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ERROR, WARNING } from 'services/common/Constants';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import { getSecurityRatingGlobalScoreGraphColors, getSecurityRatingLetterScoreGraphColors, SecurityRatingContext, securityRatingDocumentCode, SecurityRatingPerformanceValueForLetter, SecurityRatingScoresEnum } from 'services/common/SecurityRatingUtils';
import { Context } from 'states/Store';
import useStyles from 'style/js-style/components/thirdparties/securityRating/SecurityRatingStyle';
import SecurityRatingHistoryModal from './SecurityRatingHistoryModal';
import SecurityRatingStopEvaluationButton from './SecurityRatingStopEvaluationButton';
import { getThirdpartyDocuments } from 'services/edge/ThirdpartyService';

const HistoryBtnTooltipTitle = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.historyBtnTooltipTitle}>
      <EAIcon icon="warning-snack" />
      {i18next.t('securityRating.historyBtnTooltipTitle')}
    </div>
  );
};

const SecurityRatingScoresActions = ({
  data,
  securityRatingHistory,
  onFormUpdateClick = () => {},
}) => {
  const [isHistoricDialogOpen, setIsHistoricDialogOpen] = useState(false);
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const { onRiskChange } = useContext(SecurityRatingContext);

  const goToReport = async () => {
    try {
      const accountId = state.account.id;
      const thirdpartyId = state.thirdparty.thirdpartyId;
      const result = await getThirdpartyDocuments(
        state.account.id,
        state.thirdparty.thirdpartyId,
        securityRatingDocumentCode
      );
      const document = result.content?.[0];
      const evidenceId = document?.evidences?.[0]?.responseId;
      const dossierId = document?.dossierId;
      if (evidenceId && dossierId) {
        const docViewerPath = `thirdparties/${thirdpartyId}/documents/${evidenceId}?dossierId=${dossierId}`;
        window.open(getUrlWithAccountId(accountId, docViewerPath), '_blank');
      } else {
        dispatch({
          type: 'ALERT',
          alert: {
            type: WARNING,
            msg: i18next.t("securityRating.messages.reportInProgress"),
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: {
          type: ERROR,
          msg: error?.message?.messages?.error?.[0]?.code || 'INTERNAL_ERROR',
        },
      });
    }
  };

  const fetchAndOpenScoreEvolution = () => {
    setIsHistoricDialogOpen(true);
  };

  const buildGlobalScoreHistoryData = (result, score, date) => {
    if (score) {
      if (!result['global_score']) result['global_score'] = [];

      result['global_score'].push(({
        date: new Date(date).getTime(),
        year: new Date(date).getFullYear(),
        score: score.value,
        text: i18next.t('score') + " " + i18next.t(`securityRating.maturity.${score.maturity?.toLowerCase()}`),
        ...getSecurityRatingGlobalScoreGraphColors(score.maturity),
      }));
    }

    return result;
  }

  const buildLetterScoresHistoryData = (result, record) => {
    Object.values(SecurityRatingScoresEnum).forEach((score) => {
      const scoreValue = record[`${score}_score`]?.letter_value;
      const scoreValueNumber = SecurityRatingPerformanceValueForLetter[scoreValue];
      if (scoreValue) {
        if (!result[score]) result[score] = [];
        result[score].push({
          date: new Date(record.date).getTime(),
          year: new Date(record.date).getFullYear(),
          score: scoreValueNumber,
          text: i18next.t("score") + " " + scoreValue,
          ...getSecurityRatingLetterScoreGraphColors(scoreValue),
        });
      }
    });

    return result;
  }

  // Build history graph data from each record and for each score
  const historyGraphData = useMemo(() => {
    return securityRatingHistory?.sort((a, b) => new Date(a.date) - new Date(b.date))
      ?.reduce((acc, record) => {

        // Global Score from 0 to 1000
        acc = buildGlobalScoreHistoryData(acc, record.score, record.date);

        // Other scores with Letters
        acc = buildLetterScoresHistoryData(acc, record);
        return acc;
      }, {});
  }, [securityRatingHistory]);

  const isEvaluationButtonDisabled = useCallback(() => {
    return (
      !state.thirdparty?.onboarded ||
      !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE)
    );
  }, [state.thirdparty, hasAnyOfPrivileges]);

  const isHistoryButtonDisabled = () => {
    return isEmpty(historyGraphData);
  };

  return (
    <>
      <EAButton
        size="small"
        onClick={goToReport}
        colorVariant="secondary"
        content={
          <>
            <span>{i18next.t('goToReport')}</span>
            <EAIcon icon="open-page" />
          </>
        }
      />
      <EATooltip
        title={isHistoryButtonDisabled() && <HistoryBtnTooltipTitle />}
        variant="light"
        arrow
        placement="top"
      >
        <span>
          <EAButton
            size="small"
            onClick={() => fetchAndOpenScoreEvolution()}
            disabled={isHistoryButtonDisabled()}
            colorVariant="secondary_dark"
            content={
              <>
                <EAIcon icon="history" />
                <span>{i18next.t('history')}</span>
              </>
            }
          />
        </span>
      </EATooltip>
      <EAButton
        size="small"
        onClick={onFormUpdateClick}
        colorVariant="primary"
        customStyle={{ boxShadow: "none", minHeight: "30px" }}
        disabled={isEvaluationButtonDisabled()}
        content={
          <>
            <EAIcon icon="refresh" />
            <span>{i18next.t('securityRating.updateRating')}</span>
          </>
        }
      />

      <SecurityRatingStopEvaluationButton
        disabled={isEvaluationButtonDisabled()}
        onRiskChange={onRiskChange}
      />

      <SecurityRatingHistoryModal isOpen={isHistoricDialogOpen} historyData={historyGraphData} onClose={() => setIsHistoricDialogOpen(false)} />
    </>
  );
};

export default SecurityRatingScoresActions;
